import type { Edge, Node } from '@xyflow/react'

/**
 * 节点类型枚举（三层：输入 / 生成 / 输出）
 *
 * 输入类：注入素材或上下文
 *   context       — Bible/Shot 上下文元信息
 *   text          — AI 文本生成（外置指令 + 模型 → 输出提示词/旁白等，可接下游）
 *   prompt        — AI 提示词（正/负向）
 *   upload        — 用户本地上传图/视频/音频
 *   ref_character — 角色参考（多图 + 锁定 hint）
 *   ref_scene     — 场景/道具参考
 *
 * 生成类：自带 prompt 输入框 + 参数面板
 *   gen_image  — 图片生成（文生图 / 图生图，可选参考图输入）
 *   gen_video  — 视频生成（文生视频 / 图生视频，可选参考图输入）
 *   gen_audio  — 音频合成（TTS/BGM/SFX）
 *
 * 输出类：登记或合成导出
 *   register — 登记到资产库
 *   export   — 视频+音频合成导出
 */
export type StudioNodeType =
  // 输入
  | 'context'
  | 'text'
  | 'prompt'
  | 'upload'
  | 'ref_character'
  | 'ref_scene'
  // 生成
  | 'gen_image'
  | 'gen_video'
  | 'gen_audio'
  // 输出
  | 'register'
  | 'export'
  // 容器
  | 'group'

/** 旧节点 → 新节点映射（用于加载已保存画布时迁移） */
export const LEGACY_NODE_MAP: Record<string, StudioNodeType> = {
  text_to_image: 'gen_image',
  image_to_image: 'gen_image',
  text_to_video: 'gen_video',
  image_to_video: 'gen_video',
  character_lock: 'ref_character',
}

export const GEN_NODE_TYPES: StudioNodeType[] = ['gen_image', 'gen_video', 'gen_audio']

export function taskTypeForNode(nodeType: StudioNodeType): 'image' | 'video' | 'audio' | null {
  if (nodeType === 'gen_image') return 'image'
  if (nodeType === 'gen_video') return 'video'
  if (nodeType === 'gen_audio') return 'audio'
  return null
}

export function outputDataTypeForNode(nodeType: StudioNodeType): HandleDataType {
  if (nodeType === 'gen_video') return 'video'
  if (nodeType === 'gen_audio') return 'audio'
  return 'image'
}

export function isGenNodeType(nodeType: StudioNodeType): boolean {
  return GEN_NODE_TYPES.includes(nodeType)
}

export function acceptsReferenceImages(nodeType: StudioNodeType): boolean {
  return nodeType === 'gen_image' || nodeType === 'gen_video'
}

export type NodeCategory = 'input' | 'generate' | 'output' | 'container'

export function nodeCategory(type: StudioNodeType): NodeCategory {
  switch (type) {
    case 'context':
    case 'text':
    case 'prompt':
    case 'upload':
    case 'ref_character':
    case 'ref_scene':
      return 'input'
    case 'gen_image':
    case 'gen_video':
    case 'gen_audio':
      return 'generate'
    case 'register':
    case 'export':
      return 'output'
    case 'group':
      return 'container'
  }
}

export type CanvasCtx =
  | 'bible-character'
  | 'bible-location'
  | 'bible-prop'
  | 'bible-costume'
  | 'bible-style'
  | 'shot-frame'
  | 'shot-take'
  | 'free'

/** Handle 上流动的数据类型，用于连接强校验与视觉分形分色 */
export type HandleDataType = 'text' | 'image' | 'video' | 'audio' | 'ctx'

export interface UpstreamPreview {
  nodeId: string
  label: string
  url?: string
  /** 该上游产出的数据类型，用于在节点内分组展示 */
  dataType?: HandleDataType
}

export interface NodeTake {
  version: number
  status: string
  cost: number
  modelId: string
  modelName?: string
  previewUrl?: string
  taskId?: string
  createdAt: string
}

export interface TextState {
  /** 生成结果（下游 out-text 读取此字段） */
  content: string
  /** 外置输入框：用户指令（如「写一段赛博朋克场景提示词」） */
  instruction?: string
}

export interface PromptState {
  positive: string
  negative?: string
}

export interface UploadState {
  /** 已上传素材的访问 URL（可能是 OSS URL 或 data: URL） */
  url?: string
  /** MIME 类型，用于决定输出 handle 的数据类型 */
  mime?: string
  /** 原始文件名 */
  filename?: string
  /** 上传后登记到的资产 public_id（若走 uploadProductionAsset） */
  assetPublicId?: string
  /** 分镜工作台 seed 的场 scope */
  sceneScopeKey?: string
  seededFromWorkbench?: boolean
}

export interface RefAssetState {
  /** 参考图 URL 列表（角色/场景可多张） */
  urls: string[]
  /** 名称（角色名/场景名） */
  name?: string
  /** 一致性 hint */
  hint?: string
  /** 分镜工作台 seed 的场 scope */
  sceneScopeKey?: string
  seededFromWorkbench?: boolean
}

export interface GenState {
  modelId: string
  params: Record<string, string | number>
}

/** 音频节点四 Tab：旁白 / 克隆 / 设计 / 音乐 */
export type AudioProductTab = 'narration' | 'voice_clone' | 'voice_design' | 'music'

export interface AudioTabState {
  modelId: string
  params: Record<string, string | number>
  takes?: NodeTake[]
  preferredTakeVersion?: number
}

export interface AudioNodeState {
  activeTab: AudioProductTab
  tabs: Record<AudioProductTab, AudioTabState>
}

export interface OutputState {
  status: 'idle' | 'pending' | 'running' | 'succeeded' | 'failed'
  previewUrl?: string
  /** 输出数据类型（运行后写入，供下游识别） */
  dataType?: HandleDataType
  assetId?: string
  error?: string
}

export interface RegisterState {
  kind?: string
  name?: string
  registeredAssetId?: string
  /** 分镜登记时关联的镜头 public_id */
  shotPublicId?: string
}

export interface ExportState {
  /** 合成或外部成片 URL */
  outputUrl?: string
  /** 影片名称（默认同节点 label） */
  title?: string
  /** 关联的 shot public_id（写入时间线） */
  shotPublicId?: string
  status: 'idle' | 'running' | 'succeeded' | 'failed'
  error?: string
}

export interface StudioNodeData extends Record<string, unknown> {
  nodeType: StudioNodeType
  label: string
  /** 上游缩略图（保存时可冗余，加载后也会重算） */
  upstreamPreviews?: UpstreamPreview[]
  context?: {
    ctx: CanvasCtx
    entityId?: string
    /** 分镜 intent：单镜 */
    shotPublicId?: string
    /** 视频 intent：勾选批次 */
    shotPublicIds?: string[]
    shotSlug?: string
    title?: string
    /** 完整正向提示词（分镜/视频脚本） */
    prompt?: string
    intent?: 'storyboard' | 'video' | 'selection'
  }
  /** prompt 字段：prompt 节点与所有生成类节点共用 */
  prompt?: PromptState
  /** 通用文本节点内容 */
  text?: TextState
  upload?: UploadState
  refCharacter?: RefAssetState
  refScene?: RefAssetState
  gen?: GenState
  /** gen_audio 专用：四 Tab 独立 model/params/takes */
  audio?: AudioNodeState
  output?: OutputState
  takes?: NodeTake[]
  preferredTakeVersion?: number
  register?: RegisterState
  export?: ExportState
  groupMeta?: {
    title: string
    color: string
    collapsed?: boolean
  }
}

export type StudioShellNode = Node<StudioNodeData, 'studio'>
export type StudioGroupNode = Node<StudioNodeData, 'group'>
export type StudioFlowNode = StudioShellNode | StudioGroupNode
export type StudioFlowEdge = Edge<{ animated?: boolean }, 'studio'>

export interface CanvasViewport {
  x: number
  y: number
  zoom: number
}

export interface StudioCanvasGraph {
  nodes: StudioFlowNode[]
  edges: StudioFlowEdge[]
  viewport: CanvasViewport
  revision?: number
}

/** 节点分类的视觉色（左侧色条 / 头部徽章） */
export const CATEGORY_COLORS: Record<NodeCategory, string> = {
  input: '#3b82f6',
  generate: '#8b5cf6',
  output: '#f59e0b',
  container: '#52525b',
}

/** 节点类型 → 主色（用于状态点、mini map） */
export const NODE_TYPE_COLORS: Record<StudioNodeType, string> = {
  context: '#6366f1',
  text: '#c084fc',
  prompt: '#a855f7',
  upload: '#0ea5e9',
  ref_character: '#d946ef',
  ref_scene: '#ec4899',
  gen_image: '#22d3ee',
  gen_video: '#34d399',
  gen_audio: '#f472b6',
  register: '#fbbf24',
  export: '#f97316',
  group: '#52525b',
}

/** Handle 数据类型 → 颜色 */
export const HANDLE_COLORS: Record<HandleDataType, string> = {
  text: '#a855f7',
  image: '#22d3ee',
  video: '#34d399',
  audio: '#f472b6',
  ctx: '#6366f1',
}
